import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import * as eqpToolLifeApi from '../../lib/api/eqpToolLife';
import { getEqpStateStyle } from './eqpState';
import {
  TIMELINE_RANGES, rangeToFromTo, buildTimeTicks, buildStateBars, buildToolBars, buildMacroMarks, timeToX,
} from './eqpToolLifeFormat';

const REFRESH_MS = 60000;

// 도메인(viewBox) 단위 - 실제 픽셀이 아니라 SVG 좌표 단위. 폭이 좁아지면(모바일) svg 전체가
// min-width 로 가로 스크롤되고, viewBox 가 비율을 유지하며 줄어든다(EqpDayTimeline 과 다른 방식 -
// 여기는 라벨도 SVG 안에 같이 그려서 통째로 스크롤돼야 한다).
const WIDTH_UNITS = 1100;
const HEIGHT_UNITS = 160;
const LANE_LABEL_W = 60;
const RIGHT_MARGIN = 16;
const PLOT_W = WIDTH_UNITS - LANE_LABEL_W - RIGHT_MARGIN;
const MIN_PX_WIDTH = 760;

const STATE_LANE_Y = 16;
const STATE_LANE_H = 16;
const TOOL_LANE_Y = 50;
const TOOL_LANE_H = 18;
const MACRO_LANE_Y = 84;
const MACRO_M06_H = 20;
const MACRO_OTHER_H = 12;
const GRID_TOP = 10;
const GRID_BOTTOM = 112;
const TICK_LABEL_Y = 132;

/**
 * 매거진 탭 하단 - 운전 상태 / 주축 공구 / 매크로(M06 교환) 타임라인. 시안(g104-magazine.html)의
 * SVG 구성을 그대로 따르되 커스텀 폰트/색 토큰 대신 앱 MUI 테마 색을 쓴다.
 * 범위(오늘/3일/7일) 토글은 이 컴포넌트가 들고, 바뀌면 60초 새로고침 타이머를 그 범위로 다시 건다.
 */
const EqpToolTimeline = ({ eqpUuid }) => {
  const theme = useTheme();
  const [range, setRange] = useState('today');
  const [data, setData] = useState(null);
  const [bounds, setBounds] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!eqpUuid) return;
    const { from, to } = rangeToFromTo(range);
    try {
      const res = await eqpToolLifeApi.getToolTimeline(eqpUuid, { from, to });
      setData(eqpToolLifeApi.extractInfo(res));
      setBounds({ t0: dayjs(from).valueOf(), t1: dayjs(to).valueOf() });
    } catch (e) {
      // 조용히 - 다음 주기에 다시
    }
  }, [eqpUuid, range]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const ticks = bounds ? buildTimeTicks(bounds.t0, bounds.t1) : [];
  const stateBars = bounds ? buildStateBars(data?.state, bounds.t0, bounds.t1, PLOT_W) : [];
  const toolBars = bounds ? buildToolBars(data?.tools, bounds.t0, bounds.t1, PLOT_W) : [];
  const macroMarks = bounds ? buildMacroMarks(data?.macros, bounds.t0, bounds.t1, PLOT_W) : [];

  const muted = theme.palette.text.secondary;
  const line = theme.palette.divider;
  const accent = theme.palette.primary.main;
  const tickX = (ms) => LANE_LABEL_W + (bounds ? timeToX(ms, bounds.t0, bounds.t1, PLOT_W) : 0);

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.5 }}>
        <ToggleButtonGroup
          size="small" exclusive value={range}
          onChange={(_, v) => v && setRange(v)}
        >
          {TIMELINE_RANGES.map((r) => (
            <ToggleButton key={r.key} value={r.key} sx={{ px: 1.25, py: 0.25, fontSize: 11 }}>
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {!bounds ? (
        <Typography variant="body2" color="text.secondary">불러오는 중...</Typography>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <svg
            viewBox={`0 0 ${WIDTH_UNITS} ${HEIGHT_UNITS}`}
            width="100%" height={HEIGHT_UNITS}
            style={{ display: 'block', minWidth: MIN_PX_WIDTH }}
            role="img" aria-label="운전 상태, 주축 공구 구간, 매크로 호출 시점 타임라인"
          >
            {/* 세로 눈금선 */}
            {ticks.map((t) => (
              <line
                key={t.ms}
                x1={tickX(t.ms)} x2={tickX(t.ms)}
                y1={GRID_TOP} y2={GRID_BOTTOM}
                stroke={line} strokeDasharray="2 3"
              />
            ))}
            {ticks.map((t) => (
              <text
                key={`lbl-${t.ms}`}
                x={tickX(t.ms)}
                y={TICK_LABEL_Y} fontSize={11} textAnchor="middle" fill={muted}
              >
                {t.label}
              </text>
            ))}

            {/* 운전 상태 lane */}
            <text x={0} y={STATE_LANE_Y + 12} fontSize={12} fill={muted}>운전</text>
            <rect x={LANE_LABEL_W} y={STATE_LANE_Y} width={PLOT_W} height={STATE_LANE_H} fill={theme.palette.grey[100]} />
            {stateBars.map((b, i) => {
              const style = getEqpStateStyle(theme, b.val, false);
              return (
                <rect
                  key={`st-${i}`}
                  x={LANE_LABEL_W + b.x} y={STATE_LANE_Y} width={b.w} height={STATE_LANE_H}
                  fill={style.main}
                >
                  <title>{`${style.label} · ${dayjs(b.stDt).format('MM-DD HH:mm')}~${b.edDt ? dayjs(b.edDt).format('HH:mm') : '진행중'}`}</title>
                </rect>
              );
            })}

            {/* 주축 공구 lane */}
            <text x={0} y={TOOL_LANE_Y + 13} fontSize={12} fill={muted}>주축 공구</text>
            {toolBars.map((b, i) => (
              <g key={`tool-${i}`}>
                <rect
                  x={LANE_LABEL_W + b.x} y={TOOL_LANE_Y} width={Math.max(1, b.w - 1)} height={TOOL_LANE_H} rx={3}
                  fill={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.primary.light}
                  fillOpacity={0.25}
                  stroke={accent}
                >
                  <title>{`T${b.toolNo} · ${dayjs(b.stDt).format('MM-DD HH:mm')}~${b.edDt ? dayjs(b.edDt).format('HH:mm') : '진행중'}`}</title>
                </rect>
                {b.w > 20 && (
                  <text x={LANE_LABEL_W + b.x + 6} y={TOOL_LANE_Y + 13} fontSize={12} fontWeight={600} fill={accent}>
                    {`T${b.toolNo}`}
                  </text>
                )}
              </g>
            ))}

            {/* 매크로 lane - O9001(M06 교환)은 굵게, 그 외 O9xxx는 얇게 */}
            <text x={0} y={MACRO_LANE_Y + 10} fontSize={12} fill={muted}>매크로</text>
            {macroMarks.map((m, i) => {
              const h = m.isM06 ? MACRO_M06_H : MACRO_OTHER_H;
              const y = m.isM06 ? MACRO_LANE_Y : MACRO_LANE_Y + 8;
              const w = m.isM06 ? 2 : 1;
              return (
                <rect
                  key={`mac-${i}`}
                  x={LANE_LABEL_W + m.x - w / 2} y={y} width={w} height={h}
                  fill={m.isM06 ? accent : theme.palette.grey[500]}
                >
                  <title>{`${m.val} · ${dayjs(m.stDt).format('MM-DD HH:mm:ss')}`}</title>
                </rect>
              );
            })}
          </svg>
        </Box>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }} useFlexGap>
        <LaneLegend color={theme.palette.success.main} label="운전" />
        <LaneLegend color={theme.palette.warning.main} label="일시정지" />
        <LaneLegend color={theme.palette.grey[700]} label="정지" />
        <LaneLegend color={accent} label="O9001 (M06 교환)" />
        <LaneLegend color={theme.palette.grey[500]} label="O9912·O9026·O9027·O9029 (교환 전후 매크로)" />
      </Stack>
    </Box>
  );
};

const LaneLegend = ({ color, label }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);

export default EqpToolTimeline;
